import {useEffect, useState} from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  pointer-events: auto;
`;

const Spotlight = styled.div<{$top: number; $left: number; $width: number; $height: number}>`
  position: absolute;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  border-radius: 12px;
  pointer-events: auto;
  transition: all 0.4s ease-out;
  border: 2px solid #00ff88;
  box-shadow: 0 0 0 4px rgba(0, 255, 136, 0.2);
`;

const TutorialCard = styled.div<{$top: number; $left: number}>`
  position: absolute;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  background: #121212;
  border-radius: 16px;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  pointer-events: auto;
  transition: all 0.4s ease-out;
  border: 1px solid rgba(0, 255, 136, 0.3);
`;

const Header = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
`;

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.01em;
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 0.375rem;
  justify-content: flex-start;
  margin-top: 0.75rem;
`;

const Dot = styled.div<{$active: boolean}>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => (props.$active ? '#00ff88' : 'rgba(255, 255, 255, 0.2)')};
  transition: all 0.2s ease;
`;

const Content = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
`;

const Description = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 0.75rem 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Footer = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button<{$variant?: 'primary' | 'secondary'}>`
  flex: 1;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  border: none;
  background: ${props => (props.$variant === 'primary' ? '#00ff88' : 'rgba(255, 255, 255, 0.08)')};
  color: ${props => (props.$variant === 'primary' ? '#000000' : 'rgba(255, 255, 255, 0.6)')};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.01em;

  &:hover {
    background: ${props => (props.$variant === 'primary' ? '#00ffaa' : 'rgba(255, 255, 255, 0.12)')};
    color: ${props => (props.$variant === 'primary' ? '#000000' : '#ffffff')};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

interface TutorialOverlayProps {
  onClose: () => void;
}

interface TutorialStep {
  title: string;
  content: JSX.Element;
  selector: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const tutorialSteps: TutorialStep[] = [
  {
    title: 'VR Preview Window',
    selector: '[data-tutorial="preview"]',
    position: 'bottom',
    content: (
      <>
        <Description>Shows how the exercise will appear in each eye before entering VR.</Description>
      </>
    ),
  },
  {
    title: 'Motion Selector',
    selector: '[data-tutorial="motion"]',
    position: 'top',
    content: (
      <>
        <Description>Choose which exercise to practice: Convergence, Pursuit, Saccades, and more.</Description>
      </>
    ),
  },
  {
    title: 'Speed Control',
    selector: '[data-tutorial="speed"]',
    position: 'top',
    content: (
      <>
        <Description>Controls how fast the ball moves during exercises.</Description>
      </>
    ),
  },
  {
    title: 'Range Control',
    selector: '[data-tutorial="range"]',
    position: 'top',
    content: (
      <>
        <Description>Adjusts how far the ball travels in its movement pattern.</Description>
      </>
    ),
  },
  {
    title: 'Eye Settings',
    selector: '[data-tutorial="eye-controls"]',
    position: 'top',
    content: (
      <>
        <Description>
          Controls what each eye sees. Choose different colors or add markers to train your brain to favor one eye.
        </Description>
      </>
    ),
  },
  {
    title: 'Start Button',
    selector: '[data-tutorial="start"]',
    position: 'bottom',
    content: (
      <>
        <Description>
          Click to enter VR and begin your vision training session. You must be wearing a VR headset to start the
          exercise.
        </Description>
      </>
    ),
  },
];

export const TutorialOverlay: React.FC<TutorialOverlayProps> = ({onClose}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState({top: 0, left: 0, width: 0, height: 0});
  const [tooltipPosition, setTooltipPosition] = useState({top: 0, left: 0});

  useEffect(() => {
    const step = tutorialSteps[currentStep];
    const element = document.querySelector(step.selector);

    if (element) {
      const rect = element.getBoundingClientRect();
      const padding = 8;

      setSpotlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });

      // Position tooltip based on step.position
      const tooltipWidth = 360;
      const tooltipHeight = 200; // Approximate
      let top = 0;
      let left = 0;

      switch (step.position) {
        case 'bottom':
          top = rect.bottom + 16;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'top':
          top = rect.top - tooltipHeight - 16;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - 16;
          break;
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + 16;
          break;
      }

      // Keep tooltip on screen
      const maxLeft = window.innerWidth - tooltipWidth - 16;
      const maxTop = window.innerHeight - tooltipHeight - 16;
      left = Math.max(16, Math.min(left, maxLeft));
      top = Math.max(16, Math.min(top, maxTop));

      setTooltipPosition({top, left});
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = tutorialSteps[currentStep];

  return (
    <Overlay onClick={onClose}>
      <Spotlight
        $top={spotlightRect.top}
        $left={spotlightRect.left}
        $width={spotlightRect.width}
        $height={spotlightRect.height}
      />
      <TutorialCard $top={tooltipPosition.top} $left={tooltipPosition.left} onClick={e => e.stopPropagation()}>
        <Header>
          <Title>{step.title}</Title>
          <StepIndicator>
            {tutorialSteps.map((_, index) => (
              <Dot key={index} $active={index === currentStep} />
            ))}
          </StepIndicator>
        </Header>
        <Content>{step.content}</Content>
        <Footer>
          <Button onClick={handlePrevious} disabled={currentStep === 0}>
            Previous
          </Button>
          <Button $variant="primary" onClick={handleNext}>
            {currentStep === tutorialSteps.length - 1 ? 'Done' : 'Next'}
          </Button>
        </Footer>
      </TutorialCard>
    </Overlay>
  );
};
